import React, { useState } from "react";
import { Pencil, Trash2 } from "lucide-react";
import ImageUploading from "react-images-uploading";
import config from "../../config";

const ProjectDetails = ({ project, onDelete, onUpdate }) => {
  const BASE_URL = `${config.API_BASE_URL}/uploads/`;
  const fr = project.translations.find((t) => t.language_code === "fr") || {};
  const en = project.translations.find((t) => t.language_code === "en") || {};

  const [formData, setFormData] = useState({
    title_fr: fr.title || "",
    description_fr: fr.description || "",
    title_en: en.title || "",
    description_en: en.description || "",
  });

  const [images, setImages] = useState([
    ...(project.imageUrl ? [{ imageUrl: project.imageUrl }] : []),
    ...(project.secondaryImages?.map((img) => ({ id: img.id, imageUrl: img.imageUrl })) || []),
  ]);

  const maxImages = 10;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async () => {
    const formDataToSend = new FormData();
  
  
    // Vérification console de tout ce qu'on envoie
    formDataToSend.append("id", project.id);
    formDataToSend.append("category", project.category || "");
    formDataToSend.append("title_fr", formData.title_fr);
    formDataToSend.append("description_fr", formData.description_fr);
    formDataToSend.append("title_en", formData.title_en);
    formDataToSend.append("description_en", formData.description_en);
  
    const currentIds = images.map((img) => img.id).filter(Boolean);
    const originalIds = (project.secondaryImages || []).map((img) => img.id);
    const deletedIds = originalIds.filter((id) => !currentIds.includes(id));
  
    // Liste des images supprimées
    deletedIds.forEach((id) =>
      formDataToSend.append("deletedSecondaryImageIds[]", id)
    );
  
    // Image principale
    const mainImage = images[0];
    if (mainImage?.file) {
      formDataToSend.append("mainImage", mainImage.file);
    }
  
    // Images secondaires
    for (let i = 1; i < images.length; i++) {
      const img = images[i];
      if (img?.file) {
        formDataToSend.append("additionalImages", img.file);
      }
    }
  
    // 🔍 Afficher tous les champs pour debug
    for (let [key, value] of formDataToSend.entries()) {
      console.log(`${key}:`, value);
    }
  
    try {
      const response = await fetch(`${config.API_BASE_URL}/api/projects`, {
        method: "PUT",
        body: formDataToSend,
      });
  
      const data = await response.json();
  
      if (!response.ok || !data.isSuccess) {
        throw new Error("Erreur de mise à jour");
      }
  
      alert("Projet mis à jour avec succès !");
      onUpdate(); // 👈 Ajout ici pour rafraîchir la liste
    } catch (error) {
      console.error("Erreur lors de la mise à jour :", error);
      alert("Échec de la mise à jour");
    }
  };
  

  return (
    <div className="bg-white p-8 shadow-lg w-full flex flex-col space-y-6 text-black">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label htmlFor="title_fr" className="block text-sm font-semibold mb-1">
            Titre (FR)
          </label>
          <input
            id="title_fr"
            type="text"
            name="title_fr"
            placeholder="Titre (FR)"
            value={formData.title_fr}
            onChange={handleChange}
            className="p-3 border rounded-lg w-full focus:outline-none focus:ring-2 focus:ring-accent"
          />
        </div>

        <div>
          <label htmlFor="title_en" className="block text-sm font-semibold mb-1">
            Titre (EN)
          </label>
          <input
            id="title_en"
            type="text"
            name="title_en"
            placeholder="Titre (EN)"
            value={formData.title_en}
            onChange={handleChange}
            className="p-3 border rounded-lg w-full focus:outline-none focus:ring-2 focus:ring-accent"
          />
        </div>

        <div>
          <label htmlFor="description_fr" className="block text-sm font-semibold mb-1">
            Description (FR)
          </label>
          <textarea
            id="description_fr"
            name="description_fr"
            placeholder="Description (FR)"
            value={formData.description_fr}
            onChange={handleChange}
            className="p-3 border rounded-lg resize-none h-28 w-full focus:outline-none focus:ring-2 focus:ring-accent"
          />
        </div>

        <div>
          <label htmlFor="description_en" className="block text-sm font-semibold mb-1">
            Description (EN)
          </label>
          <textarea
            id="description_en"
            name="description_en"
            placeholder="Description (EN)"
            value={formData.description_en}
            onChange={handleChange}
            className="p-3 border rounded-lg resize-none h-28 w-full focus:outline-none focus:ring-2 focus:ring-accent"
          />
        </div>
      </div>

      <div className="space-y-2">
        <label className="block font-semibold text-gray-700">Images du projet</label>
        <ImageUploading
          multiple
          value={images}
          onChange={setImages}
          maxNumber={maxImages}
          dataURLKey="data_url"
        >
          {({ imageList, onImageUpload, onImageRemoveAll, onImageUpdate, onImageRemove }) => (
            <div className="flex flex-col items-start space-y-4">
              <div className="flex space-x-4">
                <button
                  type="button"
                  onClick={onImageUpload}
                  className="bg-accent text-white px-4 py-2 rounded-lg hover:bg-deepblue transition"
                >
                  Ajouter des images
                </button>
                {imageList.length > 0 && (
                  <button
                    type="button"
                    onClick={onImageRemoveAll}
                    className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition"
                  >
                    Supprimer toutes
                  </button>
                )}
              </div>
              <div className="flex flex-wrap gap-4 mt-2">
                {imageList.map((image, index) => {
                  const imageUrl = image.data_url
                    ? image.data_url
                    : image.imageUrl
                    ? `${BASE_URL}${image.imageUrl}`
                    : "";
                  return (
                    <div
                      key={index}
                      className="relative group w-32 h-32 rounded-lg overflow-hidden border shadow-sm"
                    >
                      <img
                        src={imageUrl}
                        alt=""
                        className="object-cover w-full h-full"
                      />

                      <div className="absolute inset-0 bg-black bg-opacity-50 opacity-0 group-hover:opacity-100 flex items-center justify-center space-x-3 transition">
                        <button
                          onClick={() => onImageUpdate(index)}
                          title="Modifier"
                          className="text-white hover:text-accent p-1"
                        >
                          <Pencil size={18} />
                        </button>

                        <button
                          onClick={() => onImageRemove(index)}
                          title="Supprimer"
                          className="text-white hover:text-red-500 p-1"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                      <span className="absolute bottom-0 left-0 bg-white text-xs text-gray-700 px-1 rounded-tr">
                        {index === 0 ? "Principale" : "Secondaire"}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </ImageUploading>
      </div>

      <div className="flex space-x-4">
        <button
          onClick={handleSubmit}
          className="w-full bg-accent text-white p-3 rounded-lg font-semibold hover:bg-deepblue transition"
        >
          Enregistrer les modifications
        </button>

        <button
          onClick={() => onDelete(project)}
          className="w-full bg-red-600 text-white p-3 rounded-lg font-semibold hover:bg-red-700 transition"
        >
          Supprimer le projet
        </button>
      </div>
    </div>
  );
};

export default ProjectDetails;
